/**
 * FinancialAnalysisPage.js - Análise Financeira Real
 * BLOCO 3: Controlo de Lucro e Stock
 * 
 * Funcionalidades:
 * - Histórico de Fornecedores & Preços
 * - Controlo de Lucro Real
 * - Rotação de Stock (FIFO)
 * - Produtos Sem Saída
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { 
  TrendingUp, TrendingDown, Package, Package2, AlertTriangle,
  BarChart3, PieChart, Store, Truck, Clock, CheckCircle, Users,
  Receipt, Wallet, Layers3, ListFilter, Gamepad2, ShoppingBag, Target, Scale
} from 'lucide-react';
import { formatCurrency, cn } from '@/lib/utils';
import GlobalFilters from '@/components/GlobalFilters';
import { EmptyStatePanel } from '@/components/common/EmptyStatePanel';
import { ModuleStateBoundary } from '@/components/common/ModuleStateBoundary';
import { ModulePageHero } from '@/components/common/ModulePageHero';
import { DASHBOARD_SURFACE_CLASSNAME, DashboardStatCard } from '@/components/common/DashboardStatCard';
import { useGlobalFilters } from '@/hooks/useGlobalFilters';
import { useFetchTelemetry } from '@/hooks/useFetchTelemetry';
import { safeRequest } from '@/lib/safeRequest';
import { expensesAPI, catalogsAPI } from '@/lib/api';
import { getStoredAuthToken } from '@/lib/authStorage';
import { useLanguage } from '@/i18n';
import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_BACKEND_URL;
const api = axios.create({ baseURL: API_BASE_URL });
api.interceptors.request.use(config => {
  const token = getStoredAuthToken();
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

const financialAPI = {
  // Supplier Analysis
  getSupplierPrices: (productId) => api.get(`/api/financial-analysis/supplier-prices/${productId}`),
  compareSuppliers: () => api.get('/api/financial-analysis/supplier-comparison'),
  
  // Profit Analysis
  getProfitByProduct: (params) => api.get('/api/financial-analysis/profit/by-product', { params }),
  getProfitByService: (params) => api.get('/api/financial-analysis/profit/by-service', { params }),
  getProfitByLocation: (params) => api.get('/api/financial-analysis/profit/by-location', { params }),
  getProfitByEmployee: (params) => api.get('/api/financial-analysis/profit/by-employee', { params }),
  
  // Stock Analysis
  getStockRotation: (params) => api.get('/api/financial-analysis/stock/rotation', { params }),
  getStaleStock: (params) => api.get('/api/financial-analysis/stock/stale', { params }),
  getStockAlerts: () => api.get('/api/financial-analysis/stock/alerts'),
  getCapitalTied: (params) => api.get('/api/financial-analysis/stock/capital-tied', { params }),
  
  // Margin Analysis
  getMarginAnalysis: (params) => api.get('/api/financial-analysis/margin-analysis', { params }),
  getDetailedIndicators: (params) => api.get('/api/financial-analysis/detailed-indicators', { params })
};

const ERP_CARD = DASHBOARD_SURFACE_CLASSNAME;
const ERP_INSET = 'rounded-sm border border-slate-200 bg-slate-50';
const ERP_NUM = 'font-mono tabular-nums tracking-tight';
const ERP_LABEL = 'text-sm font-medium text-slate-500';
const ERP_ROW = 'flex items-center justify-between gap-3 rounded-sm border border-slate-200 bg-slate-50 px-2.5 py-1.5';
const ERP_VALUE_CLAMP = 'font-mono tabular-nums tracking-tight text-[clamp(0.95rem,0.75rem+0.45vw,1.2rem)]';

const FINANCIAL_ACCENTS = {
  blue: {
    icon: 'from-blue-500 to-indigo-600',
    header: 'from-blue-50 via-white to-indigo-50',
    soft: 'border-blue-100 bg-blue-50/80',
    softText: 'text-blue-700',
    pill: 'border-blue-200 bg-blue-100 text-blue-700',
  },
  emerald: {
    icon: 'from-emerald-500 to-teal-600',
    header: 'from-emerald-50 via-white to-teal-50',
    soft: 'border-emerald-100 bg-emerald-50/80',
    softText: 'text-emerald-700',
    pill: 'border-emerald-200 bg-emerald-100 text-emerald-700',
  },
  orange: {
    icon: 'from-orange-500 to-amber-600',
    header: 'from-orange-50 via-white to-amber-50',
    soft: 'border-orange-100 bg-orange-50/80',
    softText: 'text-orange-700',
    pill: 'border-orange-200 bg-orange-100 text-orange-700',
  },
  purple: {
    icon: 'from-purple-500 to-violet-600',
    header: 'from-violet-50 via-white to-purple-50',
    soft: 'border-violet-100 bg-violet-50/80',
    softText: 'text-violet-700',
    pill: 'border-violet-200 bg-violet-100 text-violet-700',
  },
  rose: {
    icon: 'from-rose-500 to-red-600',
    header: 'from-rose-50 via-white to-red-50',
    soft: 'border-rose-100 bg-rose-50/80',
    softText: 'text-rose-700',
    pill: 'border-rose-200 bg-rose-100 text-rose-700',
  },
  amber: {
    icon: 'from-amber-500 to-orange-600',
    header: 'from-amber-50 via-white to-orange-50',
    soft: 'border-amber-100 bg-amber-50/80',
    softText: 'text-amber-700',
    pill: 'border-amber-200 bg-amber-100 text-amber-700',
  },
  cyan: {
    icon: 'from-cyan-500 to-blue-600',
    header: 'from-cyan-50 via-white to-blue-50',
    soft: 'border-cyan-100 bg-cyan-50/80',
    softText: 'text-cyan-700',
    pill: 'border-cyan-200 bg-cyan-100 text-cyan-700',
  },
  slate: {
    icon: 'from-slate-700 to-slate-900',
    header: 'from-slate-50 via-white to-slate-100',
    soft: 'border-slate-200 bg-slate-50/90',
    softText: 'text-slate-700',
    pill: 'border-slate-200 bg-slate-100 text-slate-700',
  },
};

const getFinancialAccent = (accent) => FINANCIAL_ACCENTS[accent] || FINANCIAL_ACCENTS.blue;

const FinancialSectionCard = ({ title, description, icon: Icon, accent = 'blue', badge, testId, children, action }) => {
  const accentMeta = getFinancialAccent(accent);

  return (
    <Card className={cn(ERP_CARD, 'overflow-hidden')} data-testid={testId}>
      <CardHeader className={cn('border-b border-slate-200/70 bg-gradient-to-br p-4 sm:p-5', accentMeta.header)}>
        <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
          <div className="min-w-0 space-y-2">
            <div className="flex flex-wrap items-center gap-3">
              <div className={cn('rounded-2xl bg-gradient-to-br p-3.5 text-white shadow-lg', accentMeta.icon)}>
                <Icon className="h-5 w-5" />
              </div>
              {badge ? <Badge variant="outline" className="rounded-full border-slate-200 bg-white/80 px-3 py-1 text-[10px] uppercase tracking-[0.14em] text-slate-600">{badge}</Badge> : null}
            </div>
            <div>
              <CardTitle className="text-lg font-semibold tracking-tight text-slate-900">{title}</CardTitle>
              {description ? <CardDescription className="mt-1 max-w-3xl text-xs leading-relaxed text-slate-600">{description}</CardDescription> : null}
            </div>
          </div>
          {action ? <div className="flex flex-wrap items-center gap-2">{action}</div> : null}
        </div>
      </CardHeader>
      <CardContent className="space-y-4 p-4 sm:p-5">{children}</CardContent>
    </Card>
  );
};

const FinancialMetricTile = ({ label, value, helper, icon: Icon, accent = 'blue', testId, valueClassName, footer = null }) => {
  const accentMeta = getFinancialAccent(accent);

  return (
    <div className="rounded-2xl border border-slate-200/80 bg-white/90 p-4 shadow-sm transition-all duration-300 hover:-translate-y-0.5 hover:shadow-md" data-testid={testId}>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="text-[11px] uppercase tracking-[0.16em] text-slate-500">{label}</p>
          {helper ? <p className="mt-1 text-xs leading-relaxed text-slate-500">{helper}</p> : null}
        </div>
        <div className={cn('shrink-0 rounded-2xl bg-gradient-to-br p-2.5 text-white shadow-md', accentMeta.icon)}>
          <Icon className="h-4 w-4" />
        </div>
      </div>
      <p className={cn('mt-4 text-xl font-semibold text-slate-950', ERP_NUM, valueClassName)}>{value}</p>
      {footer ? <div className="mt-3">{footer}</div> : null}
    </div>
  );
};

const FinancialListRow = ({
  title,
  subtitle,
  value,
  icon: Icon,
  accent = 'blue',
  testId,
  valueClassName,
  badges = [],
  leading = null,
  valueHelper = null,
  rowClassName = '',
}) => {
  const accentMeta = getFinancialAccent(accent);

  return (
    <div className={cn('flex flex-col gap-3 rounded-2xl border border-slate-200/80 bg-white/90 p-4 shadow-sm transition-all duration-300 hover:-translate-y-0.5 hover:shadow-md sm:flex-row sm:items-center sm:justify-between', rowClassName)} data-testid={testId}>
      <div className="flex min-w-0 items-start gap-3">
        {leading || (
          <div className={cn('shrink-0 rounded-2xl bg-gradient-to-br p-2.5 text-white shadow-md', accentMeta.icon)}>
            <Icon className="h-4 w-4" />
          </div>
        )}
        <div className="min-w-0">
          <p className="text-sm font-semibold text-slate-900">{title}</p>
          {subtitle ? <p className="mt-1 text-xs leading-relaxed text-slate-500">{subtitle}</p> : null}
          {badges.length ? <div className="mt-2 flex flex-wrap gap-1.5">{badges}</div> : null}
        </div>
      </div>
      <div className="sm:text-right">
        <p className={cn('text-base font-semibold text-slate-900', ERP_NUM, valueClassName)}>{value}</p>
        {valueHelper ? <p className="mt-1 text-xs text-slate-500">{valueHelper}</p> : null}
      </div>
    </div>
  );
};

export default function FinancialAnalysisPage() {
  const { t, language } = useLanguage();
  const [activeTab, setActiveTab] = useState('profit');
  const [loading, setLoading] = useState(true);
  const [minimumMarginPct, setMinimumMarginPct] = useState(() => {
    const stored = window.localStorage.getItem('financial-analysis-minimum-margin-pct');
    return stored ? Number(stored) : 20;
  });
  
  // Data states
  const [profitByProduct, setProfitByProduct] = useState(null);
  const [profitByService, setProfitByService] = useState(null);
  const [profitByLocation, setProfitByLocation] = useState(null);
  const [profitByEmployee, setProfitByEmployee] = useState(null);
  const [supplierComparison, setSupplierComparison] = useState([]);
  const [stockRotation, setStockRotation] = useState(null);
  const [staleStock, setStaleStock] = useState(null);
  const [stockAlerts, setStockAlerts] = useState(null);
  const [capitalTied, setCapitalTied] = useState(null);
  const [marginAnalysis, setMarginAnalysis] = useState(null);
  const [detailedIndicators, setDetailedIndicators] = useState(null);
  const [expenseCategories, setExpenseCategories] = useState([]);
  const [expensePgtAnalytics, setExpensePgtAnalytics] = useState(null);
  const [expenseCategoryFilter, setExpenseCategoryFilter] = useState('all');
  const [expenseSubcategoryFilter, setExpenseSubcategoryFilter] = useState('all');
  const { partialIssues, captureFetchTelemetry, resetFetchTelemetry } = useFetchTelemetry();
  
  const { filters, setFilters } = useGlobalFilters({
    period: 'all',
    location_id: null
  });

  const formatMaputoDate = (value) => {
    if (!value) return t('financialAnalysis.noData');
    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime())) return t('financialAnalysis.noData');
    return new Intl.DateTimeFormat(language === 'pt' ? 'pt-MZ' : 'en-GB', {
      timeZone: 'Africa/Maputo',
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    }).format(parsed);
  };

  useEffect(() => {
    window.localStorage.setItem('financial-analysis-minimum-margin-pct', String(minimumMarginPct));
  }, [minimumMarginPct]);

  const loadData = useCallback(async () => {
    const failedRequests = [];
    try {
      setLoading(true);
      resetFetchTelemetry();
      const params = {
        period: filters.period,
        location_id: filters.location_id,
        employee_id: filters.employee_id,
        minimum_margin_pct: minimumMarginPct,
      };
      
      const expenseAnalyticsParams = {
        period: filters.period,
        location_id: filters.location_id,
        analysis_scope: 'financial',
      };
      const resolvedSubcategoryFilter = expenseCategoryFilter === 'all'
        ? 'all'
        : expenseSubcategoryFilter;
      if (expenseCategoryFilter !== 'all') expenseAnalyticsParams.category_id = expenseCategoryFilter;
      if (resolvedSubcategoryFilter !== 'all') expenseAnalyticsParams.subcategory_id = resolvedSubcategoryFilter;

      const [productRes, serviceRes, locationRes, employeeRes, suppliersRes, rotationRes, staleRes, alertsRes, capitalRes, marginRes, indicatorsRes, expenseCategoriesRes, expenseAnalyticsRes] = await Promise.all([
        safeRequest(() => financialAPI.getProfitByProduct(params), { data: { products: [], totals: {}, warnings: [] } }, { onError: () => failedRequests.push('profit-by-product'), telemetry: { module: 'financial-analysis', endpoint: 'profit.by-product', onEvent: captureFetchTelemetry } }),
        safeRequest(() => financialAPI.getProfitByService(params), { data: { totals: {}, expense_allocation: { allocated_total_expenses: {} } } }, { onError: () => failedRequests.push('profit-by-service'), telemetry: { module: 'financial-analysis', endpoint: 'profit.by-service', onEvent: captureFetchTelemetry } }),
        safeRequest(() => financialAPI.getProfitByLocation(params), { data: { totals: {}, locations: [], warnings: {} } }, { onError: () => failedRequests.push('profit-by-location'), telemetry: { module: 'financial-analysis', endpoint: 'profit.by-location', onEvent: captureFetchTelemetry } }),
        safeRequest(() => financialAPI.getProfitByEmployee(params), { data: { employees: [], totals: {} } }, { onError: () => failedRequests.push('profit-by-employee'), telemetry: { module: 'financial-analysis', endpoint: 'profit.by-employee', onEvent: captureFetchTelemetry } }),
        safeRequest(() => financialAPI.compareSuppliers(), { data: [] }, { onError: () => failedRequests.push('supplier-comparison'), telemetry: { module: 'financial-analysis', endpoint: 'supplier.comparison', onEvent: captureFetchTelemetry } }),
        safeRequest(() => financialAPI.getStockRotation({ period: 'last_3_months', location_id: filters.location_id }), { data: { high_rotation_products: [], high_rotation_count: 0, low_rotation_products: [], low_rotation_count: 0 } }, { onError: () => failedRequests.push('stock-rotation'), telemetry: { module: 'financial-analysis', endpoint: 'stock.rotation', onEvent: captureFetchTelemetry } }),
        safeRequest(() => financialAPI.getStaleStock({ days_without_sale: 30, location_id: filters.location_id }), { data: { stale_products: [], total_stale_products: 0, total_stale_value: 0, recommendations: [], summary: {} } }, { onError: () => failedRequests.push('stale-stock'), telemetry: { module: 'financial-analysis', endpoint: 'stock.stale', onEvent: captureFetchTelemetry } }),
        safeRequest(() => financialAPI.getStockAlerts(), { data: { alerts: [], by_priority: { critical: 0, high: 0, medium: 0 }, summary: {}, total_alerts: 0 } }, { onError: () => failedRequests.push('stock-alerts'), telemetry: { module: 'financial-analysis', endpoint: 'stock.alerts', onEvent: captureFetchTelemetry } }),
        safeRequest(() => financialAPI.getCapitalTied({ location_id: filters.location_id }), { data: { summary: { total_value_at_cost: 0, total_units: 0 }, items: [], total_capital_tied: 0 } }, { onError: () => failedRequests.push('capital-tied'), telemetry: { module: 'financial-analysis', endpoint: 'stock.capital-tied', onEvent: captureFetchTelemetry } }),
        safeRequest(() => financialAPI.getMarginAnalysis(params), { data: { products: [], summary: {}, configuration: { minimum_margin_pct: minimumMarginPct }, underperforming_count: 0 } }, { onError: () => failedRequests.push('margin-analysis'), telemetry: { module: 'financial-analysis', endpoint: 'margin.analysis', onEvent: captureFetchTelemetry } }),
        safeRequest(() => financialAPI.getDetailedIndicators(params), { data: { current: {}, comparison: {}, highlights: {}, monthly_trend: [] } }, { onError: () => failedRequests.push('detailed-indicators'), telemetry: { module: 'financial-analysis', endpoint: 'detailed-indicators', onEvent: captureFetchTelemetry } }),
        safeRequest(() => catalogsAPI.listExpenseCategories(), { data: [] }, { onError: () => failedRequests.push('expense-categories') }),
        safeRequest(() => expensesAPI.getPgtAnalytics(expenseAnalyticsParams), { data: { summary: {}, by_category: [], by_subcategory: [] } }, { onError: () => failedRequests.push('expense-pgt-analytics') }),
      ]);
      
      setProfitByProduct(productRes.data);
      setProfitByService(serviceRes.data);
      setProfitByLocation(locationRes.data);
      setProfitByEmployee(employeeRes.data);
      setSupplierComparison(suppliersRes.data || []);
      setStockRotation(rotationRes.data);
      setStaleStock(staleRes.data);
      setStockAlerts(alertsRes.data);
      setCapitalTied(capitalRes.data);
      setMarginAnalysis(marginRes.data);
      setDetailedIndicators(indicatorsRes.data);
      setExpenseCategories(Array.isArray(expenseCategoriesRes.data) ? expenseCategoriesRes.data : expenseCategoriesRes.data || []);
      setExpensePgtAnalytics(expenseAnalyticsRes.data || null);
      if (failedRequests.length) {
        toast.info(t('financialAnalysis.partialPanelsToast'));
      }
    } catch (error) {
      console.error('Failed to load financial data:', error);
      toast.error(t('financialAnalysis.loadError'));
    } finally {
      setLoading(false);
    }
  }, [captureFetchTelemetry, expenseCategoryFilter, expenseSubcategoryFilter, filters, minimumMarginPct, resetFetchTelemetry, t]);

  useEffect(() => {
    void loadData();
  }, [loadData]);

  const locationTotals = profitByLocation?.totals || {};
  const indicatorsCurrent = detailedIndicators?.current || {};
  const indicatorsComparison = detailedIndicators?.comparison || {};
  const indicatorsHighlights = detailedIndicators?.highlights || {};
  const monthlyTrend = detailedIndicators?.monthly_trend || [];
  const topLevelExpenseCategories = expenseCategories.filter((category) => !category.parent_id);
  const expenseSubcategoryOptions = expenseCategoryFilter === 'all'
    ? []
    : (topLevelExpenseCategories.find((category) => category.id === expenseCategoryFilter)?.subcategories || []);
  const effectiveExpenseSubcategoryFilter = expenseCategoryFilter === 'all'
    ? 'all'
    : (expenseSubcategoryFilter !== 'all' && !expenseSubcategoryOptions.some((subcategory) => subcategory.id === expenseSubcategoryFilter)
      ? 'all'
      : expenseSubcategoryFilter);
  const monthlyTrendCompact = monthlyTrend.slice(-3);
  const maxMonthlyRevenue = Math.max(...monthlyTrendCompact.map((item) => item.revenue || 0), 1);
  const profitabilitySignal = indicatorsHighlights.profitability_signal || 'healthy';
  const profitabilitySignalLabel = {
    healthy: t('financialAnalysis.signalHealthy'),
    warning: t('financialAnalysis.signalWarning'),
    critical: t('financialAnalysis.signalCritical'),
  }[profitabilitySignal] || profitabilitySignal;
  const stockAlertTotal = stockAlerts?.total_alerts ?? stockAlerts?.summary?.total_alerts ?? (stockAlerts?.alerts || []).length;
  const stockAlertByPriority = stockAlerts?.by_priority || stockAlerts?.summary?.by_priority || {};
  const moduleAllocationCards = [
    { key: 'commercial', label: t('financialAnalysis.moduleCommercial'), data: profitByService?.commercial, accent: 'blue' },
    { key: 'projects', label: t('financialAnalysis.moduleProjects'), data: profitByService?.projects, accent: 'amber' },
    { key: 'repairs', label: t('financialAnalysis.moduleRepairs'), data: profitByService?.repairs, accent: 'orange' },
    { key: 'kits', label: t('financialAnalysis.moduleKits'), data: profitByService?.kits, accent: 'emerald' },
    { key: 'gaming', label: t('financialAnalysis.moduleGaming'), data: profitByService?.gaming, accent: 'purple' },
    { key: 'papelaria', label: t('financialAnalysis.modulePapelaria'), data: profitByService?.papelaria, accent: 'sky' },
  ];

  const renderDeltaBadge = (value, suffix = '%') => {
    const numericValue = Number(value || 0);
    const positive = numericValue >= 0;
    const Icon = positive ? TrendingUp : TrendingDown;
    return (
      <Badge className={cn('gap-1 rounded-sm border px-1.5 py-0 text-[10px] font-medium', positive ? 'border-emerald-200 bg-emerald-50 text-emerald-700' : 'border-red-200 bg-red-50 text-red-700')}>
        <Icon className="h-3 w-3" />
        {positive ? '+' : ''}{numericValue.toFixed(1)}{suffix}
      </Badge>
    );
  };

  const financialSummaryCards = [
    {
      title: t('financialAnalysis.totalRevenue'),
      value: locationTotals.total_revenue || 0,
      change: indicatorsComparison.revenue_change_pct,
      icon: TrendingUp,
      color: 'blue',
      emphasis: 'primary',
      subtitle: t('dashboard.previousPeriod'),
      testId: 'financial-summary-total-revenue-card',
    },
    {
      title: t('financialAnalysis.operationalProfit'),
      value: locationTotals.total_profit || 0,
      change: indicatorsComparison.net_profit_change_pct,
      icon: (locationTotals.total_profit || 0) >= 0 ? TrendingUp : TrendingDown,
      color: (locationTotals.total_profit || 0) >= 0 ? 'green' : 'orange',
      emphasis: 'primary',
      subtitle: t('dashboard.previousPeriod'),
      testId: 'financial-summary-operational-profit-card',
    },
    {
      title: t('financialAnalysis.totalCostExpenses'),
      value: locationTotals.total_cost || 0,
      change: indicatorsComparison.expenses_change_pct,
      icon: AlertTriangle,
      color: 'orange',
      subtitle: t('dashboard.previousPeriod'),
      testId: 'financial-summary-total-cost-card',
    },
    {
      title: t('financialAnalysis.staleStock'),
      value: staleStock?.total_stale_value || 0,
      icon: Clock,
      color: 'orange',
      subtitle: t('financialAnalysis.productsCount', { count: staleStock?.total_stale_products || 0 }),
      testId: 'financial-summary-stale-stock-card',
    },
    {
      title: t('financialAnalysis.tiedCapital'),
      value: capitalTied?.summary?.total_value_at_cost || 0,
      icon: Package,
      color: 'purple',
      subtitle: t('financialAnalysis.unitsCount', { count: capitalTied?.summary?.total_units || 0 }),
      testId: 'financial-summary-tied-capital-card',
    },
    {
      title: t('financialAnalysis.averageTicket'),
      value: indicatorsCurrent.average_ticket || 0,
      change: indicatorsComparison.average_ticket_change_pct,
      icon: BarChart3,
      color: 'cyan',
      subtitle: t('financialAnalysis.consideredMoves', { count: indicatorsCurrent.transaction_count || 0 }),
      testId: 'financial-summary-average-ticket-card',
    },
  ];

  const hasFinancialData = Boolean(
    (profitByProduct?.products || profitByProduct?.items || []).length ||
    (supplierComparison || []).length ||
    (monthlyTrend || []).length ||
    staleStock?.total_stale_products ||
    stockAlerts?.alerts?.length ||
    (profitByEmployee?.employees || []).length ||
    (profitByLocation?.locations || []).length ||
    Object.keys(locationTotals || {}).length
  );

  return (
    <ModuleStateBoundary
      loading={loading}
      hasData={hasFinancialData}
      partialIssues={partialIssues}
      loadingTestId="financial-analysis-loading-state"
      partialIssuesTestId="financial-analysis-partial-issues"
      emptyState={
        <div className="bg-slate-100 p-3 space-y-3" data-testid="financial-analysis-page">
          <EmptyStatePanel
            title={t('financialAnalysis.emptyTitle')}
            description={t('financialAnalysis.emptyDescription')}
            actionLabel={t('actions.refresh')}
            onAction={loadData}
            testId="financial-analysis-empty-state"
          />
        </div>
      }
    >
    <div className="bg-slate-100 p-4 space-y-5 lg:p-5" data-testid="financial-analysis-page">
      <ModulePageHero
        title={t('financialAnalysis.title')}
        description={t('financialAnalysis.description')}
        icon={BarChart3}
        badge={t('financialAnalysis.badge')}
        stats={[]}
        compact
        className="rounded-sm border-slate-200 shadow-none"
        testId="financial-analysis-hero"
      />

      {/* Global Filters */}
      <GlobalFilters 
        filters={['period', 'location', 'employee']}
        onChange={(newFilters) => setFilters(newFilters)}
        defaultPeriod={filters.period}
        compact
        className="rounded-sm border border-slate-200 bg-white p-2"
        filterTriggerClassName="h-7 rounded-sm text-[11px]"
        actionButtonClassName="h-7 rounded-sm text-[11px]"
        activeBadgeClassName="rounded-sm px-1.5 py-0 text-[10px]"
      />

      {/* Summary KPIs */}
      <div className="grid gap-4 xl:grid-cols-12" data-testid="financial-summary-cards-grid">
        <div className="xl:col-span-6">
          <DashboardStatCard {...financialSummaryCards[0]} />
        </div>
        <div className="xl:col-span-6">
          <DashboardStatCard {...financialSummaryCards[1]} />
        </div>
        <div className="grid gap-4 md:grid-cols-2 xl:col-span-12 xl:grid-cols-4">
          {financialSummaryCards.slice(2).map((card) => (
            <DashboardStatCard key={card.testId} {...card} />
          ))}
        </div>
      </div>

      <div className="grid gap-4 xl:grid-cols-[1.15fr_0.85fr]">
        <FinancialSectionCard
          title={t('financialAnalysis.detailedIndicators')}
          description="Leitura rápida da eficiência financeira do período, mantendo a mesma sensação premium do Painel Principal."
          icon={PieChart}
          accent="blue"
          badge="Core KPIs"
          testId="financial-detailed-indicators-card"
        >
          <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-4">
            <FinancialMetricTile
              label={t('financialAnalysis.grossMargin')}
              value={`${Number(indicatorsCurrent.gross_margin_pct || 0).toFixed(1)}%`}
              helper="Eficiência bruta antes da pressão das despesas operacionais."
              icon={PieChart}
              accent="blue"
              testId="financial-indicator-gross-margin"
              footer={renderDeltaBadge(indicatorsComparison.gross_margin_change_pts || 0, ' pts')}
            />
            <FinancialMetricTile
              label={t('financialAnalysis.netMargin')}
              value={`${Number(indicatorsCurrent.net_margin_pct || 0).toFixed(1)}%`}
              helper="Resultado líquido depois do peso operacional consolidado."
              icon={TrendingUp}
              accent="emerald"
              testId="financial-indicator-net-margin"
              footer={renderDeltaBadge(indicatorsComparison.net_margin_change_pts || 0, ' pts')}
            />
            <FinancialMetricTile
              label={t('financialAnalysis.averageTicket')}
              value={formatCurrency(indicatorsCurrent.average_ticket || 0)}
              helper="Valor médio por movimento validado no período filtrado."
              icon={BarChart3}
              accent="purple"
              testId="financial-indicator-average-ticket"
              footer={renderDeltaBadge(indicatorsComparison.average_ticket_change_pct || 0)}
            />
            <FinancialMetricTile
              label={t('financialAnalysis.expenseWeight')}
              value={`${Number(indicatorsCurrent.expense_ratio_pct || 0).toFixed(1)}%`}
              helper={t('financialAnalysis.consideredMoves', { count: indicatorsCurrent.transaction_count || 0 })}
              icon={Receipt}
              accent="orange"
              testId="financial-indicator-expense-ratio"
            />
          </div>
        </FinancialSectionCard>

        <FinancialSectionCard
          title={t('financialAnalysis.quickComparisons')}
          description="Resumo compacto para perceber direcção, pressão de custos e qualidade da mistura operacional."
          icon={TrendingUp}
          accent="emerald"
          badge="Snapshot"
          testId="financial-comparison-card"
        >
          <div className="grid gap-3 md:grid-cols-3">
            <FinancialMetricTile
              label={t('financialAnalysis.revenue')}
              value={formatCurrency(indicatorsCurrent.revenue || 0)}
              helper="Entrada total gerada no período actual."
              icon={TrendingUp}
              accent="blue"
              footer={renderDeltaBadge(indicatorsComparison.revenue_change_pct || 0)}
            />
            <FinancialMetricTile
              label={t('financialAnalysis.netProfit')}
              value={formatCurrency(indicatorsCurrent.net_profit || 0)}
              helper="Lucro líquido consolidado após custos e despesas."
              icon={(indicatorsCurrent.net_profit || 0) >= 0 ? CheckCircle : AlertTriangle}
              accent={(indicatorsCurrent.net_profit || 0) >= 0 ? 'emerald' : 'rose'}
              footer={renderDeltaBadge(indicatorsComparison.net_profit_change_pct || 0)}
            />
            <FinancialMetricTile
              label={t('financialAnalysis.expenses')}
              value={formatCurrency(indicatorsCurrent.expenses || 0)}
              helper="Pressão das despesas sobre a rentabilidade."
              icon={Receipt}
              accent="orange"
              footer={renderDeltaBadge(indicatorsComparison.expenses_change_pct || 0)}
            />
          </div>

          <div className="flex flex-wrap items-center gap-2" data-testid="financial-highlight-badges">
            <Badge className={cn('rounded-full border px-3 py-1 text-[10px] uppercase tracking-[0.14em]', indicatorsHighlights.profitability_signal === 'healthy' ? 'border-emerald-200 bg-emerald-100 text-emerald-700' : indicatorsHighlights.profitability_signal === 'warning' ? 'border-amber-200 bg-amber-100 text-amber-700' : 'border-rose-200 bg-rose-100 text-rose-700')}>
              {t('financialAnalysis.signal')}: {profitabilitySignalLabel}
            </Badge>
            <Badge variant="outline" className="rounded-full border-slate-200 bg-slate-50 px-3 py-1 text-[10px] uppercase tracking-[0.14em] text-slate-700">{t('financialAnalysis.serviceMix')}: {Number(indicatorsHighlights.service_mix_pct || 0).toFixed(1)}%</Badge>
            <Badge variant="outline" className="rounded-full border-slate-200 bg-slate-50 px-3 py-1 text-[10px] uppercase tracking-[0.14em] text-slate-700">{t('financialAnalysis.salesMix')}: {Number(indicatorsHighlights.sales_mix_pct || 0).toFixed(1)}%</Badge>
          </div>
        </FinancialSectionCard>
      </div>

      <Card className={ERP_CARD} data-testid="financial-monthly-trend-card">
        <CardHeader className="pb-1.5 pt-2.5">
          <CardTitle className="flex items-center gap-1.5 text-sm font-semibold tracking-tight"><BarChart3 className="h-3.5 w-3.5 text-slate-700" /> {t('financialAnalysis.monthlyTrend')}</CardTitle>
          <CardDescription className="text-[11px] leading-snug">
            {t('financialAnalysis.monthlyTrendDescription')}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-2.5 pb-2.5">
          <div className="grid gap-2 lg:grid-cols-2">
            <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-3">
              <p className={ERP_LABEL}>{t('financialAnalysis.bestMonth')}</p>
              <p className="mt-2 text-lg font-semibold text-slate-900">{indicatorsHighlights.best_month?.label || t('financialAnalysis.noData')}</p>
              <p className={cn('mt-1 text-sm text-emerald-700', ERP_NUM)}>{formatCurrency(indicatorsHighlights.best_month?.net_profit || 0)}</p>
            </div>
            <div className="rounded-xl border border-rose-200 bg-rose-50 p-3">
              <p className={ERP_LABEL}>{t('financialAnalysis.watchMonth')}</p>
              <p className="mt-2 text-lg font-semibold text-slate-900">{indicatorsHighlights.worst_month?.label || t('financialAnalysis.noData')}</p>
              <p className={cn('mt-1 text-sm text-rose-700', ERP_NUM)}>{formatCurrency(indicatorsHighlights.worst_month?.net_profit || 0)}</p>
            </div>
          </div>

          <div className="space-y-3">
            {monthlyTrendCompact.map((item) => (
              <div key={item.month_key} className="grid gap-3 rounded-2xl border border-slate-200 bg-slate-50 px-4 py-4 lg:grid-cols-[110px_minmax(0,1fr)_140px_140px] lg:items-center" data-testid={`financial-month-row-${item.month_key}`}>
                <div>
                  <p className="text-base font-semibold text-slate-900">{item.label}</p>
                  <p className="text-xs text-slate-500">{t('financialAnalysis.netMarginShort')} {Number(item.net_margin_pct || 0).toFixed(1)}%</p>
                </div>
                <div>
                  <div className="h-3 rounded-full bg-slate-200">
                    <div className="h-3 rounded-full bg-slate-900" style={{ width: `${Math.max(((item.revenue || 0) / maxMonthlyRevenue) * 100, item.revenue > 0 ? 8 : 0)}%` }} />
                  </div>
                </div>
                <div>
                  <p className={ERP_LABEL}>{t('financialAnalysis.revenue')}</p>
                  <p className={cn('text-base font-semibold text-slate-900', ERP_NUM)}>{formatCurrency(item.revenue || 0)}</p>
                </div>
                <div>
                  <p className={ERP_LABEL}>{t('financialAnalysis.netProfit')}</p>
                  <p className={cn('text-base font-semibold', ERP_NUM, (item.net_profit || 0) >= 0 ? 'text-emerald-700' : 'text-red-700')}>
                    {formatCurrency(item.net_profit || 0)}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      <FinancialSectionCard
        title="Leitura PGT das despesas"
        description="Painel mais limpo para identificar rapidamente o peso das categorias e subcategorias de despesa."
        icon={Receipt}
        accent="orange"
        badge="PGT"
        testId="financial-expense-pgt-analysis-card"
        action={(
          <div className="grid gap-2 sm:grid-cols-2 lg:min-w-[24rem]">
            <Select value={expenseCategoryFilter} onValueChange={(value) => {
              setExpenseCategoryFilter(value);
              setExpenseSubcategoryFilter('all');
            }}>
              <SelectTrigger className="h-10 rounded-xl border-slate-300 bg-white shadow-sm text-[11px]" data-testid="financial-expense-category-filter">
                <SelectValue placeholder="Todas as categorias PGT" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todas as categorias PGT</SelectItem>
                {topLevelExpenseCategories.map((category) => (
                  <SelectItem key={category.id} value={category.id}>{category.expense_code} · {category.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={effectiveExpenseSubcategoryFilter} onValueChange={setExpenseSubcategoryFilter}>
              <SelectTrigger className="h-10 rounded-xl border-slate-300 bg-white shadow-sm text-[11px]" data-testid="financial-expense-subcategory-filter">
                <SelectValue placeholder="Todas as subcategorias PGT" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todas as subcategorias PGT</SelectItem>
                {expenseSubcategoryOptions.map((subcategory) => (
                  <SelectItem key={subcategory.id} value={subcategory.id}>{subcategory.expense_code} · {subcategory.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}
      >
        <div className="grid gap-3 md:grid-cols-3">
          <FinancialMetricTile
            label="Montante analisado"
            value={formatCurrency(expensePgtAnalytics?.summary?.total_amount || 0)}
            helper={`${expensePgtAnalytics?.summary?.total_count || 0} despesas consideradas`}
            icon={Wallet}
            accent="orange"
            testId="financial-pgt-total-amount"
          />
          <FinancialMetricTile
            label="Categoria dominante"
            value={expensePgtAnalytics?.summary?.top_category?.category_code || '—'}
            helper={expensePgtAnalytics?.summary?.top_category?.category_name || 'Sem dados suficientes'}
            icon={Layers3}
            accent="blue"
            testId="financial-pgt-top-category"
          />
          <FinancialMetricTile
            label="Subcategoria dominante"
            value={expensePgtAnalytics?.summary?.top_subcategory?.subcategory_code || '—'}
            helper={expensePgtAnalytics?.summary?.top_subcategory?.subcategory_name || 'Sem dados suficientes'}
            icon={ListFilter}
            accent="purple"
            testId="financial-pgt-top-subcategory"
          />
        </div>

        <div className="grid gap-4 xl:grid-cols-2">
          <div className="rounded-2xl border border-slate-200/80 bg-slate-50/70 p-4">
            <div className="mb-3 flex items-center justify-between gap-2">
              <p className="text-sm font-semibold text-slate-900">Categorias com maior peso</p>
              <Badge variant="outline" className="rounded-full border-slate-200 bg-white px-3 py-1 text-[10px] uppercase tracking-[0.14em]">{expensePgtAnalytics?.by_category?.length || 0}</Badge>
            </div>
            <div className="space-y-2">
              {(expensePgtAnalytics?.by_category || []).slice(0, 6).map((item) => (
                <FinancialListRow
                  key={item.category_id || item.category_code}
                  title={`${item.category_code} · ${item.category_name}`}
                  subtitle={`${item.count} despesas · ${item.share_pct}% do total`}
                  value={formatCurrency(item.total)}
                  icon={Layers3}
                  accent="emerald"
                  testId={`financial-pgt-category-row-${item.category_code}`}
                  valueClassName="text-emerald-700"
                />
              ))}
            </div>
          </div>

          <div className="rounded-2xl border border-slate-200/80 bg-slate-50/70 p-4">
            <div className="mb-3 flex items-center justify-between gap-2">
              <p className="text-sm font-semibold text-slate-900">Subcategorias com maior peso</p>
              <Badge variant="outline" className="rounded-full border-slate-200 bg-white px-3 py-1 text-[10px] uppercase tracking-[0.14em]">{expensePgtAnalytics?.by_subcategory?.length || 0}</Badge>
            </div>
            <div className="space-y-2">
              {(expensePgtAnalytics?.by_subcategory || []).slice(0, 6).map((item) => (
                <FinancialListRow
                  key={item.subcategory_id || `${item.category_code}-${item.subcategory_code}`}
                  title={`${item.subcategory_code} · ${item.subcategory_name}`}
                  subtitle={`${item.category_code} · ${item.category_name} · ${item.count} despesas`}
                  value={formatCurrency(item.total)}
                  icon={ListFilter}
                  accent="purple"
                  testId={`financial-pgt-subcategory-row-${item.subcategory_code}`}
                  valueClassName="text-violet-700"
                />
              ))}
            </div>
          </div>
        </div>
      </FinancialSectionCard>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid h-auto w-full grid-cols-2 gap-1 rounded-sm bg-slate-200/70 p-1 sm:grid-cols-3 xl:grid-cols-7">
          <TabsTrigger className="h-7 rounded-sm px-2 text-[10px] font-semibold uppercase tracking-[0.14em] data-[state=active]:bg-white data-[state=active]:shadow-none" value="profit" data-testid="financial-analysis-tab-profit">{t('financialAnalysis.profitTab')}</TabsTrigger>
          <TabsTrigger className="h-7 rounded-sm px-2 text-[10px] font-semibold uppercase tracking-[0.14em] data-[state=active]:bg-white data-[state=active]:shadow-none" value="allocation" data-testid="financial-analysis-tab-allocation">{t('financialAnalysis.allocationTab')}</TabsTrigger>
          <TabsTrigger className="h-7 rounded-sm px-2 text-[10px] font-semibold uppercase tracking-[0.14em] data-[state=active]:bg-white data-[state=active]:shadow-none" value="employees" data-testid="financial-analysis-tab-employees">{t('financialAnalysis.employeesTab')}</TabsTrigger>
          <TabsTrigger className="h-7 rounded-sm px-2 text-[10px] font-semibold uppercase tracking-[0.14em] data-[state=active]:bg-white data-[state=active]:shadow-none" value="locations" data-testid="financial-analysis-tab-locations">{t('financialAnalysis.locationsTab')}</TabsTrigger>
          <TabsTrigger className="h-7 rounded-sm px-2 text-[10px] font-semibold uppercase tracking-[0.14em] data-[state=active]:bg-white data-[state=active]:shadow-none" value="margins" data-testid="financial-analysis-tab-margins">{t('financialAnalysis.marginsTab')}</TabsTrigger>
          <TabsTrigger className="h-7 rounded-sm px-2 text-[10px] font-semibold uppercase tracking-[0.14em] data-[state=active]:bg-white data-[state=active]:shadow-none" value="suppliers" data-testid="financial-analysis-tab-suppliers">{t('financialAnalysis.suppliersTab')}</TabsTrigger>
          <TabsTrigger className="h-7 rounded-sm px-2 text-[10px] font-semibold uppercase tracking-[0.14em] data-[state=active]:bg-white data-[state=active]:shadow-none" value="stock" data-testid="financial-analysis-tab-stock">{t('financialAnalysis.stockTab')}</TabsTrigger>
        </TabsList>

        {/* Profit Tab */}
        <TabsContent value="profit" className="mt-2 space-y-3">
          {/* Services Profit */}
          <FinancialSectionCard
            title={t('financialAnalysis.profitByServiceTitle')}
            description="Vista resumida do lucro operacional por tipo de serviço com melhor hierarquia visual e leitura imediata."
            icon={Package}
            accent="orange"
            badge="Services"
            testId="financial-profit-by-service-card"
          >
            <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
              <FinancialMetricTile
                label={t('financialAnalysis.moduleRepairs')}
                value={formatCurrency(profitByService?.repairs?.profit || 0)}
                helper={`${t('financialAnalysis.serviceRevenue')}: ${formatCurrency(profitByService?.repairs?.revenue || 0)} · ${t('financialAnalysis.partsCost')}: ${formatCurrency(profitByService?.repairs?.parts_cost || 0)}`}
                icon={Package}
                accent="orange"
                footer={<Badge className="rounded-full border-orange-200 bg-orange-100 px-3 py-1 text-[10px] uppercase tracking-[0.14em] text-orange-700">{t('financialAnalysis.simpleMargin', { value: (profitByService?.repairs?.margin_pct || 0).toFixed(1) })}</Badge>}
              />
              <FinancialMetricTile
                label={t('financialAnalysis.moduleGaming')}
                value={formatCurrency(profitByService?.gaming?.profit || 0)}
                helper={t('financialAnalysis.sessionsCount', { count: profitByService?.gaming?.count || 0 })}
                icon={Gamepad2}
                accent="purple"
                footer={<Badge className="rounded-full border-violet-200 bg-violet-100 px-3 py-1 text-[10px] uppercase tracking-[0.14em] text-violet-700">{t('financialAnalysis.simpleMargin', { value: '100.0' })}</Badge>}
              />
              <FinancialMetricTile
                label={t('financialAnalysis.modulePapelaria')}
                value={formatCurrency(profitByService?.papelaria?.profit || 0)}
                helper={t('financialAnalysis.ordersCount', { count: profitByService?.papelaria?.count || 0 })}
                icon={ShoppingBag}
                accent="blue"
                footer={<Badge className="rounded-full border-blue-200 bg-blue-100 px-3 py-1 text-[10px] uppercase tracking-[0.14em] text-blue-700">{t('financialAnalysis.simpleMargin', { value: '100.0' })}</Badge>}
              />
            </div>
          </FinancialSectionCard>

          {/* Products Profit */}
          <FinancialSectionCard
            title={t('financialAnalysis.profitByProductTitle')}
            description={t('financialAnalysis.topProductsSummary', {
              revenue: formatCurrency(profitByProduct?.totals?.total_revenue || 0),
              cost: formatCurrency(profitByProduct?.totals?.total_cost || 0),
              profit: formatCurrency(profitByProduct?.totals?.total_profit || 0),
            })}
            icon={Package2}
            accent="blue"
            badge="Top 10"
            testId="financial-profit-by-product-card"
          >
            {profitByProduct?.report_confidence === 'partial' && (
              <div className="rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3" data-testid="financial-by-product-partial-warning">
                <Badge className="rounded-full border-amber-200 bg-amber-100 px-3 py-1 text-[10px] uppercase tracking-[0.14em] text-amber-800">
                  {t('financialAnalysis.partialReportTitle')}
                </Badge>
                <p className="mt-2 text-xs text-amber-700">
                  {(profitByProduct?.warnings || []).slice(0, 2).join(' • ') || t('financialAnalysis.partialReportFallback')}
                </p>
              </div>
            )}
            {(profitByProduct?.products || []).slice(0, 10).length === 0 ? (
              <p className="py-8 text-center text-muted-foreground">{t('financialAnalysis.noSalesInFilteredPeriod')}</p>
            ) : (
              <div className="space-y-2">
                {(profitByProduct?.products || []).slice(0, 10).map((p, idx) => (
                  <FinancialListRow
                    key={p.product_id}
                    title={p.product_name}
                    subtitle={t('financialAnalysis.soldCount', { count: p.quantity_sold })}
                    value={formatCurrency(p.profit)}
                    icon={Package2}
                    accent={p.profit >= 0 ? 'emerald' : 'rose'}
                    testId={`financial-product-row-${p.product_id}`}
                    valueClassName={p.profit >= 0 ? 'text-emerald-700' : 'text-rose-700'}
                    leading={
                      <div className={cn('flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl text-sm font-bold text-white shadow-md', idx < 3 ? 'bg-gradient-to-br from-slate-900 to-slate-700' : 'bg-gradient-to-br from-slate-500 to-slate-700')}>
                        #{idx + 1}
                      </div>
                    }
                    badges={[
                      <Badge key="margin" className={cn('rounded-full px-3 py-1 text-[10px] uppercase tracking-[0.14em]', p.margin_pct == null ? 'border-slate-200 bg-slate-100 text-slate-700' : p.margin_pct >= 30 ? 'border-emerald-200 bg-emerald-100 text-emerald-700' : p.margin_pct >= 15 ? 'border-amber-200 bg-amber-100 text-amber-700' : 'border-rose-200 bg-rose-100 text-rose-700')}>
                        {p.margin_pct == null ? t('financialAnalysis.marginUnavailable') : t('financialAnalysis.marginValue', { value: Number(p.margin_pct).toFixed(1) })}
                      </Badge>
                    ]}
                  />
                ))}
              </div>
            )}
          </FinancialSectionCard>
        </TabsContent>

        <TabsContent value="allocation" className="mt-2 space-y-3">
          <FinancialSectionCard
            title={t('financialAnalysis.allocationTitle')}
            description={t('financialAnalysis.allocationDescription')}
            icon={Layers3}
            accent="purple"
            badge="Allocation"
            testId="financial-module-allocation-card"
          >
              <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
                {moduleAllocationCards.map((module) => (
                  <div key={module.key} className="rounded-2xl border border-slate-200/80 bg-slate-50/70 p-4 shadow-sm" data-testid={`financial-module-allocation-${module.key}`}>
                    <div className="flex items-center justify-between gap-2">
                      <div className="flex items-center gap-3">
                        <div className="rounded-2xl bg-gradient-to-br from-violet-500 to-purple-600 p-2.5 text-white shadow-md"><Layers3 className="h-4 w-4" /></div>
                        <p className="font-medium text-slate-900">{module.label}</p>
                      </div>
                      <Badge variant="outline" className="rounded-full border-slate-200 bg-white">{module.data?.count || 0}</Badge>
                    </div>
                    <div className="mt-4 space-y-2 text-sm text-slate-600">
                      <div className="flex items-center justify-between"><span>{t('financialAnalysis.revenue')}</span><span className="font-semibold text-slate-900">{formatCurrency(module.data?.revenue || 0)}</span></div>
                      <div className="flex items-center justify-between"><span>{t('financialAnalysis.directCost')}</span><span className="font-semibold text-slate-900">{formatCurrency(module.data?.parts_cost ?? module.data?.cost ?? 0)}</span></div>
                      <div className="flex items-center justify-between"><span>{t('financialAnalysis.allocatedExpenses')}</span><span className="font-semibold text-slate-900">{formatCurrency(module.data?.allocated_expenses || 0)}</span></div>
                      <div className="flex items-center justify-between"><span>{t('financialAnalysis.operationalProfitLabel')}</span><span className={cn('font-semibold', (module.data?.operational_profit || 0) >= 0 ? 'text-emerald-700' : 'text-red-700')}>{formatCurrency(module.data?.operational_profit || 0)}</span></div>
                    </div>
                    <div className="mt-4 flex flex-wrap gap-2">
                      <Badge className="rounded-full bg-slate-900 text-white">{t('financialAnalysis.marginValue', { value: Number(module.data?.margin_pct || 0).toFixed(1) })}</Badge>
                      <Badge variant="outline" className="rounded-full border-slate-200 bg-white">{t('financialAnalysis.operationalMarginValue', { value: Number(module.data?.operational_margin_pct || 0).toFixed(1) })}</Badge>
                    </div>
                  </div>
                ))}

                <div className="rounded-2xl border border-slate-200/80 bg-slate-50/70 p-4 shadow-sm" data-testid="financial-module-allocation-inventory">
                  <div className="flex items-center justify-between gap-2">
                    <div className="flex items-center gap-3">
                      <div className="rounded-2xl bg-gradient-to-br from-blue-500 to-indigo-600 p-2.5 text-white shadow-md"><Package2 className="h-4 w-4" /></div>
                      <p className="font-medium text-slate-900">{t('financialAnalysis.inventoryModule')}</p>
                    </div>
                    <Badge variant="outline" className="rounded-full border-slate-200 bg-white">{t('financialAnalysis.capitalLabel')}</Badge>
                  </div>
                  <div className="mt-4 space-y-2 text-sm text-slate-600">
                    <div className="flex items-center justify-between"><span>{t('financialAnalysis.capitalLabel')}</span><span className="font-semibold text-slate-900">{formatCurrency(capitalTied?.total_capital_tied || capitalTied?.summary?.total_value_at_cost || 0)}</span></div>
                    <div className="flex items-center justify-between"><span>{t('financialAnalysis.staleStockItems')}</span><span className="font-semibold text-slate-900">{t('financialAnalysis.itemsLabel', { count: staleStock?.summary?.stale_count || staleStock?.total_stale_products || 0 })}</span></div>
                    <div className="flex items-center justify-between"><span>{t('financialAnalysis.riskValue')}</span><span className="font-semibold text-slate-900">{formatCurrency(staleStock?.summary?.total_stale_value || staleStock?.total_stale_value || 0)}</span></div>
                    <div className="flex items-center justify-between"><span>{t('financialAnalysis.alertsLabel')}</span><span className="font-semibold text-slate-900">{stockAlerts?.summary?.critical_alerts || stockAlertByPriority.critical || 0}</span></div>
                  </div>
                </div>
              </div>

              <div className="grid gap-4 lg:grid-cols-[1fr_0.8fr]">
                <div className="rounded-2xl border border-slate-200/80 bg-slate-50/70 p-4 shadow-sm" data-testid="financial-expense-allocation-breakdown">
                  <div className="flex items-center gap-3">
                    <div className="rounded-2xl bg-gradient-to-br from-orange-500 to-amber-600 p-2.5 text-white shadow-md"><Receipt className="h-4 w-4" /></div>
                    <div>
                      <p className="text-sm font-semibold text-slate-900">{t('financialAnalysis.expenseAllocationTitle')}</p>
                      <p className="mt-1 text-xs text-slate-500">{profitByService?.expense_allocation?.allocation_rule || t('financialAnalysis.noAllocationRule')}</p>
                    </div>
                  </div>
                  <div className="mt-4 space-y-2">
                    {Object.entries(profitByService?.expense_allocation?.allocated_total_expenses || {}).map(([moduleKey, amount]) => (
                      <div key={moduleKey} className="flex items-center justify-between rounded-xl border border-white/80 bg-white/90 px-3 py-2 text-sm">
                        <span className="text-slate-600">{moduleAllocationCards.find((card) => card.key === moduleKey)?.label || moduleKey}</span>
                        <span className="font-semibold text-slate-900">{formatCurrency(amount || 0)}</span>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="rounded-2xl border border-slate-200/80 bg-slate-50/70 p-4 shadow-sm" data-testid="financial-allocation-totals-card">
                  <div className="flex items-center gap-3">
                    <div className="rounded-2xl bg-gradient-to-br from-slate-700 to-slate-900 p-2.5 text-white shadow-md"><BarChart3 className="h-4 w-4" /></div>
                    <p className="text-sm font-semibold text-slate-900">{t('financialAnalysis.allocatedTotalsTitle')}</p>
                  </div>
                  <div className="mt-4 space-y-2 text-sm">
                    <div className="flex items-center justify-between rounded-xl border border-white/80 bg-white/90 px-3 py-2"><span className="text-slate-500">{t('financialAnalysis.totalRevenueLabel')}</span><span className="font-semibold text-slate-900">{formatCurrency(profitByService?.totals?.total_revenue || 0)}</span></div>
                    <div className="flex items-center justify-between rounded-xl border border-white/80 bg-white/90 px-3 py-2"><span className="text-slate-500">{t('financialAnalysis.totalCostLabel')}</span><span className="font-semibold text-slate-900">{formatCurrency(profitByService?.totals?.total_cost || 0)}</span></div>
                    <div className="flex items-center justify-between rounded-xl border border-white/80 bg-white/90 px-3 py-2"><span className="text-slate-500">{t('financialAnalysis.grossProfitLabel')}</span><span className="font-semibold text-slate-900">{formatCurrency(profitByService?.totals?.total_profit || 0)}</span></div>
                    <div className="flex items-center justify-between rounded-xl border border-white/80 bg-white/90 px-3 py-2"><span className="text-slate-500">{t('financialAnalysis.operationalProfitLabel')}</span><span className="font-semibold text-slate-900">{formatCurrency(profitByService?.totals?.total_operational_profit || 0)}</span></div>
                    <div className="flex items-center justify-between rounded-xl border border-white/80 bg-white/90 px-3 py-2"><span className="text-slate-500">{t('financialAnalysis.allocatedExpenses')}</span><span className="font-semibold text-slate-900">{formatCurrency(profitByService?.totals?.total_allocated_expenses || 0)}</span></div>
                  </div>
                </div>
              </div>
          </FinancialSectionCard>
        </TabsContent>

        {/* Employees Tab */}
        <TabsContent value="employees" className="mt-2 space-y-3">
          <FinancialSectionCard
            title={t('financialAnalysis.employeesPerformanceTitle')}
            description={t('financialAnalysis.employeesPerformanceSummary', {
              profit: formatCurrency(profitByEmployee?.totals?.total_profit || 0),
              sales: profitByEmployee?.totals?.total_sales || 0,
              services: profitByEmployee?.totals?.total_services || 0,
            })}
            icon={Users}
            accent="blue"
            badge="Team"
            testId="financial-employees-performance-card"
          >
              {(profitByEmployee?.employees || []).length === 0 ? (
                <p className="text-center text-muted-foreground py-8">{t('financialAnalysis.noActivityInPeriod')}</p>
              ) : (
                <div className="space-y-2">
                  {(profitByEmployee?.employees || []).map((emp, idx) => (
                    <FinancialListRow
                      key={emp.employee_id || idx}
                      title={emp.employee_name}
                      subtitle={t('financialAnalysis.salesAndServicesCount', { sales: emp.sales_count, services: emp.services_count })}
                      value={formatCurrency(emp.total_profit)}
                      icon={Users}
                      accent={idx === 0 ? 'emerald' : idx === (profitByEmployee?.employees?.length || 0) - 1 && profitByEmployee?.employees?.length > 1 ? 'rose' : 'blue'}
                      testId={`financial-employee-row-${emp.employee_id || idx}`}
                      valueClassName={emp.total_profit >= 0 ? 'text-emerald-700' : 'text-rose-700'}
                      badges={[
                        <Badge key="sales" variant="outline" className="rounded-full border-slate-200 bg-slate-50 px-3 py-1 text-[10px] uppercase tracking-[0.14em]">{t('financialAnalysis.salesRevenue', { value: formatCurrency(emp.sales_revenue) })}</Badge>,
                        <Badge key="services" variant="outline" className="rounded-full border-slate-200 bg-slate-50 px-3 py-1 text-[10px] uppercase tracking-[0.14em]">{t('financialAnalysis.servicesRevenue', { value: formatCurrency(emp.services_revenue) })}</Badge>,
                        <Badge key="margin" className={cn('rounded-full px-3 py-1 text-[10px] uppercase tracking-[0.14em]', emp.margin_pct >= 30 ? 'border-emerald-200 bg-emerald-100 text-emerald-700' : emp.margin_pct >= 15 ? 'border-amber-200 bg-amber-100 text-amber-700' : 'border-rose-200 bg-rose-100 text-rose-700')}>
                          {t('financialAnalysis.marginValue', { value: (emp.margin_pct || 0).toFixed(1) })}
                        </Badge>
                      ]}
                      leading={
                        <div className={cn('flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl text-sm font-bold text-white shadow-md', idx === 0 ? 'bg-gradient-to-br from-emerald-500 to-teal-600' : idx === (profitByEmployee?.employees?.length || 0) - 1 && profitByEmployee?.employees?.length > 1 ? 'bg-gradient-to-br from-rose-500 to-red-600' : 'bg-gradient-to-br from-blue-500 to-indigo-600')}>
                          #{idx + 1}
                        </div>
                      }
                    />
                  ))}
                </div>
              )}
              
              {profitByEmployee?.top_performer && profitByEmployee?.bottom_performer && profitByEmployee?.performance_gap > 0 && (
                <div className="mt-4 rounded-2xl border border-blue-200 bg-blue-50 px-4 py-3">
                  <p className="font-medium text-blue-800">{t('financialAnalysis.performanceAnalysisTitle')}</p>
                  <p className="text-sm text-blue-700">
                    • {t('financialAnalysis.bestPerformerLabel')} <b>{profitByEmployee.top_performer.employee_name}</b> ({formatCurrency(profitByEmployee.top_performer.total_profit)})
                  </p>
                  <p className="text-sm text-blue-700">
                    • {t('financialAnalysis.performanceGapLabel')} <b>{formatCurrency(profitByEmployee.performance_gap)}</b>
                  </p>
                </div>
              )}
          </FinancialSectionCard>
        </TabsContent>

        {/* Locations Tab */}
        <TabsContent value="locations" className="mt-2 space-y-3">
          <Card className={ERP_CARD}>
            <CardHeader className="pb-2 pt-3">
              <CardTitle className="text-sm font-semibold tracking-tight">{t('financialAnalysis.storePerformanceTitle')}</CardTitle>
              {(profitByLocation?.warnings?.draining_locations || []).length > 0 && (
                <div className="flex items-center gap-2 text-red-600 text-sm">
                  <AlertTriangle className="h-4 w-4" />
                  {t('financialAnalysis.drainingLocations', { locations: profitByLocation.warnings.draining_locations.join(', ') })}
                </div>
              )}
            </CardHeader>
            <CardContent>
              {(profitByLocation?.locations || []).length === 0 ? (
                <p className="text-center text-muted-foreground py-8">{t('financialAnalysis.noStoresFound')}</p>
              ) : (
                <div className="space-y-2">
                  {(profitByLocation?.locations || []).map(loc => (
                    <div key={loc.location_id} className={cn(
                      "rounded-sm border px-2.5 py-2",
                      !loc.is_profitable && "border-red-300 bg-red-50"
                    )} data-testid={`financial-location-row-${loc.location_id}`}>
                      <div className="mb-2 flex items-start justify-between">
                        <div className="flex items-center gap-2">
                          <Store className={cn("h-3.5 w-3.5", loc.is_profitable ? "text-green-600" : "text-red-600")} />
                          <div>
                            <p className="text-sm font-medium">{loc.location_name}</p>
                            <Badge variant="outline" className="rounded-sm px-1.5 py-0 text-[10px]">{loc.location_type}</Badge>
                          </div>
                        </div>
                        <div className="text-right">
                          <p className={cn("text-sm font-bold", ERP_NUM, loc.is_profitable ? "text-green-600" : "text-red-600")}>
                            {formatCurrency(loc.profit)}
                          </p>
                          <p className="text-xs text-muted-foreground">
                            {t('financialAnalysis.simpleMargin', { value: loc.margin_pct.toFixed(1) })}
                          </p>
                        </div>
                      </div>
                      
                      <div className="grid gap-2 text-[11px] sm:grid-cols-2 xl:grid-cols-4">
                        <div>
                          <p className="text-xs text-muted-foreground">{t('financialAnalysis.salesShort')}</p>
                          <p className="font-medium">{formatCurrency(loc.sales_revenue)}</p>
                        </div>
                        <div>
                          <p className="text-xs text-muted-foreground">{t('financialAnalysis.servicesShort')}</p>
                          <p className="font-medium">{formatCurrency(loc.services_revenue)}</p>
                        </div>
                        <div>
                          <p className="text-xs text-muted-foreground">{t('financialAnalysis.moduleGaming')}</p>
                          <p className="font-medium">{formatCurrency(loc.gaming_revenue)}</p>
                        </div>
                        <div>
                          <p className="text-xs text-muted-foreground">{t('financialAnalysis.expensesShort')}</p>
                          <p className="font-medium text-red-600">{formatCurrency(loc.expenses)}</p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Margins Tab */}
        <TabsContent value="margins" className="mt-2 space-y-3">
          <FinancialSectionCard
            title={t('financialAnalysis.expectedVsActualMarginTitle')}
            description={t('financialAnalysis.gapSummary', {
              amount: formatCurrency(marginAnalysis?.summary?.profit_gap || 0),
              percent: (marginAnalysis?.summary?.gap_percentage || 0).toFixed(1),
            })}
            icon={BarChart3}
            accent="purple"
            badge="Margins"
            testId="financial-margins-card"
            action={(
              <div className="rounded-2xl border border-slate-200 bg-white p-3 lg:min-w-[260px]" data-testid="financial-analysis-minimum-margin-config-card">
                <p className="text-xs font-medium uppercase tracking-wide text-slate-500">{t('financialAnalysis.reportInfoTitle')}</p>
                <div className="mt-3 space-y-2">
                  <label htmlFor="financial-analysis-minimum-margin-input" className="text-sm font-medium text-slate-800">
                    {t('financialAnalysis.minimumRequiredMarginLabel')}
                  </label>
                  <Input
                    id="financial-analysis-minimum-margin-input"
                    type="number"
                    min="0"
                    max="100"
                    step="0.5"
                    value={minimumMarginPct}
                    onChange={(e) => setMinimumMarginPct(Number(e.target.value || 0))}
                    data-testid="financial-analysis-minimum-margin-input"
                  />
                  <p className="text-xs text-slate-500">{t('financialAnalysis.minimumRequiredMarginHint')}</p>
                </div>
              </div>
            )}
          >
              <div className="mb-3 grid gap-3 md:grid-cols-3">
                <FinancialMetricTile label={t('financialAnalysis.expectedProfitLabel')} value={formatCurrency(marginAnalysis?.summary?.total_expected_profit || 0)} icon={Target} accent="blue" />
                <FinancialMetricTile label={t('financialAnalysis.realProfitLabel')} value={formatCurrency(marginAnalysis?.summary?.total_actual_profit || 0)} icon={CheckCircle} accent="emerald" />
                <FinancialMetricTile label={t('financialAnalysis.differenceLabel')} value={formatCurrency(marginAnalysis?.summary?.profit_gap || 0)} icon={Scale} accent={marginAnalysis?.summary?.profit_gap >= 0 ? 'emerald' : 'rose'} valueClassName={marginAnalysis?.summary?.profit_gap >= 0 ? 'text-emerald-700' : 'text-rose-700'} />
              </div>

              <div className="mb-4 rounded-2xl border border-violet-200 bg-violet-50 px-4 py-3 text-sm text-violet-900" data-testid="financial-analysis-minimum-margin-info">
                {t('financialAnalysis.currentMinimumMargin', { value: (marginAnalysis?.configuration?.minimum_margin_pct ?? minimumMarginPct).toFixed(1) })}
              </div>
              
              {marginAnalysis?.underperforming_count > 0 && (
                <div className="mb-4 rounded-2xl border border-amber-200 bg-amber-50 p-3">
                  <p className="flex items-center gap-2 font-medium text-amber-800">
                    <AlertTriangle className="h-4 w-4" />
                    {t('financialAnalysis.underperformingProducts', { count: marginAnalysis.underperforming_count })}
                  </p>
                </div>
              )}

              <div className="space-y-2">
                <p className="text-sm font-medium text-muted-foreground">{t('financialAnalysis.productsMarginDeviation')}</p>
                {(marginAnalysis?.products || []).slice(0, 10).map(p => (
                  <FinancialListRow
                    key={p.product_id}
                    title={p.product_name}
                    subtitle={t('financialAnalysis.soldCount', { count: p.quantity_sold })}
                    value={`${p.margin_deviation >= 0 ? '+' : ''}${p.margin_deviation}%`}
                    icon={Scale}
                    accent={p.is_underperforming ? 'rose' : 'emerald'}
                    testId={`financial-margin-row-${p.product_id}`}
                    valueClassName={p.margin_deviation >= 0 ? 'text-emerald-700' : 'text-rose-700'}
                    badges={[
                      <Badge key="expected" variant="outline" className="rounded-full border-slate-200 bg-slate-50 px-3 py-1 text-[10px] uppercase tracking-[0.14em]">{t('financialAnalysis.expectedLabel', { value: p.expected_margin_pct })}</Badge>,
                      <Badge key="actual" className={cn('rounded-full px-3 py-1 text-[10px] uppercase tracking-[0.14em]', p.actual_margin_pct >= p.expected_margin_pct ? 'border-emerald-200 bg-emerald-100 text-emerald-700' : 'border-rose-200 bg-rose-100 text-rose-700')}>
                        {t('financialAnalysis.actualLabel', { value: p.actual_margin_pct })}
                      </Badge>
                    ]}
                  />
                ))}
              </div>
          </FinancialSectionCard>
        </TabsContent>

        {/* Suppliers Tab */}
        <TabsContent value="suppliers" className="mt-2 space-y-3">
          <FinancialSectionCard
            title={t('financialAnalysis.supplierComparisonTitle')}
            description={t('financialAnalysis.supplierComparisonDescription')}
            icon={Truck}
            accent="cyan"
            badge="Suppliers"
            testId="financial-supplier-comparison-card"
          >
              {supplierComparison.length === 0 ? (
                <p className="text-center text-muted-foreground py-8">{t('financialAnalysis.noSuppliersFound')}</p>
              ) : (
                <div className="space-y-2">
                  {supplierComparison.map((s, idx) => (
                    <FinancialListRow
                      key={s.supplier_id}
                      title={s.name}
                      subtitle={t('financialAnalysis.ordersAndProductsCount', { orders: s.total_orders, products: s.unique_products })}
                      value={formatCurrency(s.total_value)}
                      icon={Truck}
                      accent="cyan"
                      testId={`financial-supplier-row-${s.supplier_id}`}
                      valueHelper={t('financialAnalysis.averageOrderValue', { value: formatCurrency(s.avg_order_value) })}
                      leading={
                        <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl bg-gradient-to-br from-cyan-500 to-blue-600 text-sm font-bold text-white shadow-md">
                          #{idx + 1}
                        </div>
                      }
                    />
                  ))}
                </div>
              )}
          </FinancialSectionCard>
        </TabsContent>

        {/* Stock Tab */}
        <TabsContent value="stock" className="mt-2 space-y-3">
          {/* Stock Alerts */}
          {stockAlerts && stockAlertTotal > 0 && (
            <Card className="rounded-sm border-amber-300 shadow-none">
              <CardHeader className="pb-2">
                <CardTitle className="flex items-center gap-2 text-amber-600">
                  <AlertTriangle className="h-5 w-5" />
                  {t('financialAnalysis.stockAlertsTitle', { count: stockAlertTotal })}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="mb-2 flex gap-2">
                  <Badge className="rounded-sm bg-red-100 px-1.5 py-0 text-[10px] text-red-700">{t('financialAnalysis.criticalCount', { count: stockAlertByPriority.critical || 0 })}</Badge>
                  <Badge className="rounded-sm bg-orange-100 px-1.5 py-0 text-[10px] text-orange-700">{t('financialAnalysis.highCount', { count: stockAlertByPriority.high || 0 })}</Badge>
                  <Badge className="rounded-sm bg-amber-100 px-1.5 py-0 text-[10px] text-amber-700">{t('financialAnalysis.mediumCount', { count: stockAlertByPriority.medium || 0 })}</Badge>
                </div>
                <div className="space-y-2 max-h-40 overflow-y-auto">
                  {(stockAlerts.alerts || []).slice(0, 5).map((a, idx) => (
                    <div key={idx} className="flex items-center gap-2 text-sm p-2 bg-amber-50 rounded">
                      <Badge className={cn(
                        "text-xs",
                        a.priority === 'critical' ? "bg-red-100 text-red-700" :
                        a.priority === 'high' ? "bg-orange-100 text-orange-700" :
                        "bg-amber-100 text-amber-700"
                      )}>
                        {a.type}
                      </Badge>
                      <span>{a.message}</span>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Stock Rotation */}
          <div className="grid gap-3 xl:grid-cols-2">
            <Card className={ERP_CARD}>
              <CardHeader className="pb-2 pt-3">
                <CardTitle className="flex items-center gap-1.5 text-sm font-semibold tracking-tight">
                  <TrendingUp className="h-3.5 w-3.5 text-green-600" />
                  {t('financialAnalysis.highRotationTitle', { count: stockRotation?.high_rotation_count || 0 })}
                </CardTitle>
                <CardDescription>{t('financialAnalysis.highRotationDescription')}</CardDescription>
              </CardHeader>
              <CardContent>
                {(stockRotation?.high_rotation_products || []).length === 0 ? (
                  <p className="text-center text-muted-foreground py-4">{t('financialAnalysis.noProductsLabel')}</p>
                ) : (
                  <div className="space-y-1.5">
                    {(stockRotation?.high_rotation_products || []).map(p => (
                      <div key={p.product_id} className="flex items-center justify-between rounded-sm bg-green-50 px-2 py-1.5" data-testid={`financial-stock-high-row-${p.product_id}`}>
                        <div>
                          <p className="font-medium text-sm">{p.product_name}</p>
                          <p className="text-xs text-muted-foreground">{t('financialAnalysis.soldCount', { count: p.quantity_sold })}</p>
                        </div>
                        <Badge className="rounded-sm bg-green-100 px-1.5 py-0 text-[10px] text-green-700">
                          {p.rotation_rate}x
                        </Badge>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>

            <Card className={ERP_CARD}>
              <CardHeader className="pb-2 pt-3">
                <CardTitle className="flex items-center gap-1.5 text-sm font-semibold tracking-tight">
                  <TrendingDown className="h-3.5 w-3.5 text-red-600" />
                  {t('financialAnalysis.lowRotationTitle', { count: stockRotation?.low_rotation_count || 0 })}
                </CardTitle>
                <CardDescription>{t('financialAnalysis.lowRotationDescription')}</CardDescription>
              </CardHeader>
              <CardContent>
                {(stockRotation?.low_rotation_products || []).length === 0 ? (
                  <p className="text-center text-muted-foreground py-4">{t('financialAnalysis.noProductsLabel')}</p>
                ) : (
                  <div className="space-y-1.5">
                    {(stockRotation?.low_rotation_products || []).map(p => (
                      <div key={p.product_id} className="flex items-center justify-between rounded-sm bg-red-50 px-2 py-1.5" data-testid={`financial-stock-low-row-${p.product_id}`}>
                        <div>
                          <p className="font-medium text-sm">{p.product_name}</p>
                          <p className="text-xs text-muted-foreground">{t('financialAnalysis.stockShort', { count: p.current_stock })}</p>
                        </div>
                        <Badge className="rounded-sm bg-red-100 px-1.5 py-0 text-[10px] text-red-700">
                          {p.rotation_rate}x
                        </Badge>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Stale Stock */}
          <FinancialSectionCard
            title={t('financialAnalysis.staleProductsTitle')}
            description={t('financialAnalysis.staleProductsDescription', {
              count: staleStock?.total_stale_products || 0,
              value: formatCurrency(staleStock?.total_stale_value || 0),
            })}
            icon={Clock}
            accent="amber"
            badge="30+ dias"
            testId="financial-stale-products-card"
          >
              {(staleStock?.stale_products || []).length === 0 ? (
                <p className="text-center text-muted-foreground py-8">{t('financialAnalysis.noStaleProducts')}</p>
              ) : (
                <div className="max-h-60 space-y-2 overflow-y-auto pr-1">
                  {(staleStock?.stale_products || []).slice(0, 15).map(p => (
                    <FinancialListRow
                      key={p.product_id}
                      title={p.product_name}
                      subtitle={`SKU: ${p.sku} · ${t('financialAnalysis.stockShort', { count: p.current_stock })} · ${t('financialAnalysis.lastSaleLabel', { value: p.days_since_sale === 'Never sold' ? t('financialAnalysis.neverSold') : t('financialAnalysis.daysAgo', { count: p.days_since_sale }) })}`}
                      value={formatCurrency(p.stock_value)}
                      icon={Clock}
                      accent="amber"
                      testId={`financial-stale-row-${p.product_id}`}
                      valueClassName="text-amber-700"
                    />
                  ))}
                </div>
              )}
              
              {(staleStock?.recommendations || []).length > 0 && (
                <div className="mt-4 rounded-2xl border border-blue-200 bg-blue-50 p-3">
                  <p className="font-medium text-blue-800 mb-1">{t('financialAnalysis.recommendationsTitle')}</p>
                  {staleStock.recommendations.map((r, idx) => (
                    <p key={idx} className="text-sm text-blue-700">• {r}</p>
                  ))}
                </div>
              )}
          </FinancialSectionCard>
        </TabsContent>
      </Tabs>
    </div>
    </ModuleStateBoundary>
  );
}
